import { ReactElement, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomInvestigationForm,
  CustomInvestigationInputFields,
  CustomInvestigationOutputFields,
} from '../../../custom-investigation-form/CustomInvestigationForm';
import {
  AnalysisMode,
  CreateCustomInvestigationModel,
  CreateInvestigationResponse,
  useCreateInvestigationMutation,
} from '@customer/store/apis/investigationsApi';
import { DateTime } from 'luxon';
import { AnalysisModeLimits } from '../../../config';
import { useAppDispatch } from '@customer/store/hooks';
import { useNavigate } from 'react-router-dom';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { AssetBasicModel } from '@customer/store/apis/assetsApi';
import { SuccessNotificationContext } from '@dmc/ui';
import { ModelName } from '@customer/store/types';

type InputFields = CustomInvestigationInputFields;
type OutputFields = CustomInvestigationOutputFields;
type MutationArgs = CreateCustomInvestigationModel;
type MutationResponse = CreateInvestigationResponse;

export const defaultCustomInvestigationFormValues: InputFields = {
  name: '',
  startDate: null,
  startTime: null,
  duration: AnalysisModeLimits[AnalysisMode.Enhanced].maxDuration,
  analysisMode: AnalysisMode.Basic,
  oneSecLog: false,
  assetIds: [],
};

export const getCreateCustomInvestigationArgs = (fieldValues: OutputFields): MutationArgs => {
  const startDateTime = DateTime.fromISO(fieldValues.startTime);
  const endDateTime = startDateTime.plus({ minutes: fieldValues.duration });
  const start = startDateTime.toUTC().toString();
  const end = endDateTime.toUTC().toString();

  const payload: MutationArgs = {
    n: fieldValues.name,
    start,
    end,
    mode: fieldValues.analysisMode,
    oneSecLog: fieldValues.oneSecLog,
    assets: fieldValues.assetIds,
  };
  return payload;
};

interface CreateCustomInvestigationFormProps {
  assets: AssetBasicModel[];
}

export const CreateCustomInvestigationForm = ({
  assets,
}: CreateCustomInvestigationFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);
  const [triggerMutation] = useCreateInvestigationMutation();

  const successCallback = useCallback(
    (investigationId: MutationResponse) => {
      // Use the response to navigate to the new investigation.
      if (typeof investigationId === 'number') {
        showSuccessMessage(t('Model.Create.Success', { modelName: ModelName.Investigation }));
        dispatch(closeSidePanel());
        navigate(`/investigation/${investigationId}`);
      }
    },
    [dispatch, navigate, showSuccessMessage, t],
  );

  return (
    <CustomInvestigationForm<MutationArgs, MutationResponse>
      defaultValues={defaultCustomInvestigationFormValues}
      getArgs={getCreateCustomInvestigationArgs}
      triggerMutation={triggerMutation}
      successCallback={successCallback}
      assets={assets}
      submitLabel={t('Create')}
    />
  );
};
